import { useState, useEffect, useCallback } from 'react';

interface TeleprompterPersistentState {
  scriptId: string;
  scrollPosition: number;
  elapsedTime: number;
  isPlaying: boolean;
  speed: number;
  fontSize: number;
  mirrorMode: boolean;
  lineHeight: number;
  horizontalPadding: number;
  theme: 'light' | 'dark';
  voiceSyncEnabled: boolean;
  timestamp: number; // When the state was saved
}

const STORAGE_KEY = 'vespers_teleprompter_state';
const STATE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours

/**
 * Custom hook to persist and restore teleprompter state
 * Saves state to localStorage so it survives browser refresh/close
 */
export function useTeleprompterPersistence(scriptId: string | undefined) {
  const [isRestored, setIsRestored] = useState(false);

  // Save state to localStorage
  const saveState = useCallback((state: Omit<TeleprompterPersistentState, 'scriptId' | 'timestamp'>) => {
    if (!scriptId) return;

    const persistentState: TeleprompterPersistentState = {
      ...state,
      scriptId,
      timestamp: Date.now(),
    };

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(persistentState));
    } catch (err) {
      console.error('Failed to save teleprompter state:', err);
    }
  }, [scriptId]);

  // Load state from localStorage
  const loadState = useCallback((): Partial<Omit<TeleprompterPersistentState, 'scriptId' | 'timestamp'>> | null => {
    if (!scriptId) return null;

    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) return null;

      const state: TeleprompterPersistentState = JSON.parse(stored);

      // Check if state is for the same script
      if (state.scriptId !== scriptId) {
        return null;
      }

      // Check if state has expired
      if (Date.now() - state.timestamp > STATE_EXPIRY_MS) {
        localStorage.removeItem(STORAGE_KEY);
        return null;
      }

      // Return state without metadata
      const { scriptId: _, timestamp: __, ...restoredState } = state;
      return restoredState;
    } catch (err) {
      console.error('Failed to load teleprompter state:', err);
      return null;
    }
  }, [scriptId]);

  // Clear saved state
  const clearState = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (err) {
      console.error('Failed to clear teleprompter state:', err);
    }
  }, []);

  // Mark as restored after initial load attempt
  useEffect(() => {
    if (scriptId) {
      setIsRestored(true);
    }
  }, [scriptId]);

  return {
    saveState,
    loadState,
    clearState,
    isRestored,
  };
}
